interface ChartDataPoint {
  label: string
  value: number
  color?: string
  ringColor?: string
  textColor?: string
  percentage?: number
}

interface SimpleChartProps {
  title: string
  data: ChartDataPoint[]
  type: 'bar' | 'pie' | 'donut'
  className?: string
}

export default function SimpleChart({ title, data, type, className = '' }: SimpleChartProps) {
  const maxValue = Math.max(...data.map(d => d.value), 1)

  if (type === 'bar') {
    return (
      <div className={`${className}`}>
        <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-28 text-sm text-gray-700 flex-shrink-0 font-medium">
                {item.label}
              </div>
              <div className="flex-1">
                <div className="bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${item.color || 'bg-blue-500'}`}
                    style={{ width: `${(item.value / maxValue) * 100}%` }}
                  />
                </div>
              </div>
              <div className="w-8 text-sm font-semibold text-gray-800 text-right flex-shrink-0">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'donut') {
    return (
      <div className={`${className}`}>
        <h3 className="text-base font-semibold text-gray-900 mb-6">{title}</h3>
        <div className="flex items-center justify-around">
          {data.map((item, index) => (
            <div key={index} className="flex flex-col items-center gap-2">
              <div
                className={`w-24 h-24 rounded-full flex items-center justify-center ${item.ringColor || 'bg-gray-200'}`}
              >
                <span className={`text-2xl font-bold ${item.textColor || 'text-gray-700'}`}>
                  {item.value}
                </span>
              </div>
              <p className="text-sm text-gray-700 font-medium">
                {item.label} ({item.percentage ?? 0}%)
              </p>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'pie') {
    const total = data.reduce((sum, item) => sum + item.value, 0)
    return (
      <div className={`bg-white shadow rounded-lg p-6 ${className}`}>
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-2">
          {data.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0
            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${item.color || 'bg-primary-500'}`} />
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">{item.value}</span>
                  <span className="text-xs text-gray-500">({percentage}%)</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return null
}
